import { ReactNode } from "react";
import HandDrawnBracket from "../components/atoms/svgs/HandDrawnBracket";

interface RenderWithBracketsOptions {
	text: string | ReactNode;
	bracketedText?: string;
	bracketAnimateColors?: boolean;
	bracketColor?: string;
	bracketHeight?: string;
	/**
	 * Font size information for calculating responsive bracket height.
	 * If provided, bracket height will be calculated as: fontSize * lineHeightMultiplier
	 */
	fontSize?: {
		min: string; // e.g., "28px"
		preferred: string; // e.g., "3.33vw"
		max: string; // e.g., "48px"
	};
	/**
	 * Line height multiplier (defaults to 1.25 for leading-tight).
	 * Used with fontSize to calculate responsive bracket height.
	 */
	lineHeightMultiplier?: number;
}

/**
 * Calculates bracket height based on font size and line height.
 * Returns a clamp value that scales with the text size.
 */
function calculateBracketHeight(
	fontSize: { min: string; preferred: string; max: string },
	lineHeightMultiplier: number = 1.25,
): string {
	// Extract numeric values from font size strings
	const minMatch = fontSize.min.match(/(\d+(?:\.\d+)?)px/);
	const maxMatch = fontSize.max.match(/(\d+(?:\.\d+)?)px/);

	if (!minMatch || !maxMatch) {
		// Fallback if parsing fails
		return "clamp(35px, 4.16vw, 60px)";
	}

	const minFontSize = parseFloat(minMatch[1]);
	const maxFontSize = parseFloat(maxMatch[1]);

	// Calculate bracket heights based on line height
	const minHeight = minFontSize * lineHeightMultiplier;
	const maxHeight = maxFontSize * lineHeightMultiplier;

	// Calculate preferred value proportionally
	// If preferred is a vw value, scale it proportionally
	const preferredMatch = fontSize.preferred.match(/(\d+(?:\.\d+)?)vw/);
	if (preferredMatch) {
		const preferredVw = parseFloat(preferredMatch[1]);
		// Scale the vw value proportionally to line height
		const preferredHeight = preferredVw * lineHeightMultiplier;
		return `clamp(${minHeight}px, ${preferredHeight}vw, ${maxHeight}px)`;
	}

	// Fallback if preferred is not vw
	return `clamp(${minHeight}px, ${(minHeight + maxHeight) / 2}px, ${maxHeight}px)`;
}

/**
 * Renders text with optional HandDrawnBracket components wrapping a specified portion.
 * Supports both string and ReactNode input types.
 *
 * @param options - Configuration object
 * @param options.text - The full text or ReactNode to render
 * @param options.bracketedText - Optional text portion to wrap with brackets
 * @param options.bracketAnimateColors - Whether brackets should animate colors
 * @param options.bracketColor - Optional color for the brackets
 * @param options.bracketHeight - Optional explicit height for brackets (overrides fontSize calculation)
 * @param options.fontSize - Font size information for calculating responsive bracket height
 * @param options.lineHeightMultiplier - Line height multiplier (defaults to 1.25 for leading-tight)
 * @returns ReactNode with brackets applied if bracketedText is found
 */
export function renderWithBrackets({
	text,
	bracketedText,
	bracketAnimateColors = false,
	bracketColor,
	bracketHeight,
	fontSize,
	lineHeightMultiplier = 1.25,
}: RenderWithBracketsOptions): ReactNode {
	// Calculate bracket height if fontSize is provided and bracketHeight is not explicitly set
	const calculatedHeight =
		bracketHeight ||
		(fontSize
			? calculateBracketHeight(fontSize, lineHeightMultiplier)
			: "clamp(35px, 4.16vw, 60px)"); // Default fallback
	// If no bracketed text specified, render text as-is
	if (!bracketedText) {
		if (typeof text === "string") {
			return <span dangerouslySetInnerHTML={{ __html: text }} />;
		}
		return <>{text}</>;
	}

	// If text is a string, parse it and wrap the bracketed text
	if (typeof text === "string") {
		const textStr = text;
		const index = textStr.indexOf(bracketedText);

		if (index === -1) {
			// Text not found, render as-is
			return <span dangerouslySetInnerHTML={{ __html: text }} />;
		}

		// Split the text into parts
		const before = textStr.substring(0, index);
		const after = textStr.substring(index + bracketedText.length);

		// Build bracket props
		const bracketProps = {
			animateColors: bracketAnimateColors,
			height: calculatedHeight,
			...(bracketColor && { color: bracketColor }),
		};

		return (
			<>
				{before && <span dangerouslySetInnerHTML={{ __html: before }} />}
				<span className="">
					<HandDrawnBracket
						{...bracketProps}
						side="left"
						className="inline-block -mr-[6px] -mt-2 relative"
					/>
					<span dangerouslySetInnerHTML={{ __html: bracketedText }} />
					<HandDrawnBracket
						{...bracketProps}
						side="right"
						className="inline-block -ml-[10px] mr-2 -mt-2 relative"
					/>
				</span>
				{after && <span dangerouslySetInnerHTML={{ __html: after }} />}
			</>
		);
	}

	// If text is already a ReactNode, return as-is (user can include brackets manually)
	return <>{text}</>;
}
